import type { CurrentCart, LanguageIsoCode } from '@/graphql/graphql';
import {
    addToCart as apiAddToCart,
    removeFromCart as apiRemoveFromCart,
    updateCart as apiUpdateCart,
    getCart,
} from '@/lib/api-cart';
import { useCallback, useEffect, useState } from 'react';
import { CartContext } from './cart-context';
import { useUserData } from './user-data-context';

export const CartProvider = ({ children }: { children: React.ReactNode }) => {
    const [cart, setCart] = useState<CurrentCart | null>(null);
    const [isReady, setIsReady] = useState(false);
    const { isBuyer, loading: userDataLoading } = useUserData();

    useEffect(() => {
        let cancelled = false;

        if (userDataLoading) {
            setIsReady(false);
            return;
        }

        if (!isBuyer) {
            setCart(null);
            setIsReady(true);
            return;
        }

        setIsReady(false);

        (async () => {
            try {
                const nextCart = await getCart();
                if (!cancelled) setCart(nextCart);
            } catch {
                if (!cancelled) setCart(null);
            } finally {
                if (!cancelled) setIsReady(true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isBuyer, userDataLoading]);

    const refreshCart = useCallback(async () => {
        try {
            const updatedCart = await getCart();
            setCart(updatedCart);
            setIsReady(true);
            return updatedCart;
        } catch {
            setCart(null);
            setIsReady(true);
            return null;
        }
    }, []);

    const addToCart = async (programId: string, language: LanguageIsoCode, excludeEpisodeIds?: string[]) => {
        const updatedCart = await apiAddToCart(programId, language, excludeEpisodeIds);
        setCart(updatedCart);
        setIsReady(true);
    };

    const removeFromCart = async (programId: string, language: LanguageIsoCode) => {
        try {
            const updatedCart = await apiRemoveFromCart(programId, language);
            setCart(updatedCart);
            setIsReady(true);
        } catch {
            return;
        }
    };

    const updateCart = async (programId: string, currentLanguage: LanguageIsoCode, newLanguage: LanguageIsoCode) => {
        try {
            const updatedCart = await apiUpdateCart(programId, currentLanguage, newLanguage);
            setCart(updatedCart);
            setIsReady(true);
        } catch {
            return;
        }
    };

    return (
        <CartContext.Provider value={{ cart, isReady, refreshCart, addToCart, removeFromCart, updateCart }}>
            {children}
        </CartContext.Provider>
    );
};
